/**
 * ADR-0227 — shown under the export buttons after a download from a day
 * that was too large for one audit bundle. Says which part came down
 * (always the one holding the open record) and lists the other parts,
 * each with its own download. Downloads run one at a time; no new deps.
 */

import { useState } from 'react';
import { Download, Info } from 'lucide-react';

import type { AuditRecord } from '@sill/types';

import type { SillApi } from '../../api/contract.js';
import type { useC } from '../../lib/theme.js';
import { type BundleSplit, exportAuditBundlePart, exportErrorMessage } from './export.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const fmt = (n: number): string => n.toLocaleString('en-US');

export function BundlePartsNote({
  C,
  api,
  record,
  siteDomain,
  split,
}: {
  C: ReturnType<typeof useC>;
  api: SillApi;
  record: AuditRecord;
  siteDomain?: string | undefined;
  split: BundleSplit | null;
}): JSX.Element | null {
  const [busyPart, setBusyPart] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (split === null) return null;
  const m = split.manifest;
  const current = m.parts.find((p) => p.part === split.downloaded_part);
  const others = m.parts.filter((p) => p.part !== split.downloaded_part);

  const onDownload = (part: number): void => {
    setBusyPart(part);
    setError(null);
    void exportAuditBundlePart(api, record, split, part, siteDomain)
      .catch((err: unknown) => { setError(exportErrorMessage('Bundle export', err)); })
      .finally(() => { setBusyPart(null); });
  };

  return (
    <div
      className="mt-2 p-3"
      data-testid="bundle-parts-note"
      style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 2 }}
    >
      <div className="flex items-start gap-2">
        <Info size={12} color={C.sky} className="flex-shrink-0 mt-0.5" />
        <span style={{ fontFamily: sansFont, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
          This day has {fmt(m.record_count)} records, so its audit bundle is split into{' '}
          {m.part_count} parts. You downloaded part {split.downloaded_part} of {m.part_count}
          {current !== undefined
            ? ` (records ${fmt(current.first_record)}–${fmt(current.last_record)})`
            : ''}
          , which contains this record. Each part verifies on its own.
        </span>
      </div>
      <div className="mt-2" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
        {others.map((p) => (
          <div
            key={p.part}
            className="flex items-center justify-between gap-2"
            style={{ padding: '6px 0', borderBottom: `1px solid ${C.borderSoft}` }}
          >
            <span style={{ fontFamily: monoFont, fontSize: 11, color: C.body }}>
              Part {p.part} · records {fmt(p.first_record)}–{fmt(p.last_record)}
            </span>
            <button
              type="button"
              onClick={() => onDownload(p.part)}
              disabled={busyPart !== null}
              aria-label={`Download part ${String(p.part)} of ${String(m.part_count)}`}
              className="flex items-center gap-1 px-2 py-1"
              style={{
                background: 'transparent',
                color: C.text,
                border: `1px solid ${C.border}`,
                fontFamily: sansFont,
                fontWeight: 500,
                fontSize: 11,
                borderRadius: 2,
                cursor: busyPart !== null ? 'not-allowed' : 'pointer',
              }}
            >
              <Download size={11} /> {busyPart === p.part ? 'Downloading…' : `Download part ${String(p.part)}`}
            </button>
          </div>
        ))}
      </div>
      {error !== null ? (
        <div className="mt-2" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
